/**
 * 이상한 방 - 수색 판정 (사격 결과 해석 + 점수)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.5절(점수)
 *            `이상한방-상태모델-v0.1.md` 6.1절(두 종류의 히트) · 6.2절(빈 자리 판정은 관대하게) · 6.3절(옮긴 물건은 두 자리) · 12절(속도 보너스)
 *
 * ## 책임 경계 - 세 층이 각자 한 가지만 답한다
 *
 * | 층 | 답하는 질문 | 파일 |
 * |---|---|---|
 * | 기하 | 조준선이 **무엇에 닿았는가** | `input/tapPicker.ts` · `render/hitProxy.ts` |
 * | 판정 | 그것이 **정답인가** | **이 파일** |
 * | 상태 | 그래서 **탄환·점수·페이즈가 어떻게 변하는가** | `game/gameState.ts` |
 *
 * 이 파일은 순수 함수만 담는다. 씬도, 시간도, 난수도 만지지 않는다.
 * 판정이 순수해야 같은 시드 + 같은 조작 = 같은 결과가 보장되고(결정론), 그래야 리플레이·스피드런이 성립한다.
 *
 * ## 판정 키는 좌표가 아니라 `InstanceId`다
 *
 * 정답의 단위는 `Change` 하나다. 그래서 무엇을 맞혔는지는 **id**로 묻고, 그 id를 건드린 변화가 있으면
 * 정답이다. 좌표로 물었다면 "그 셀에 무엇이 있어야 하는가"를 판정기가 다시 계산해야 하고, 그 계산은
 * 화면(`render/`)이 이미 하고 있는 계산의 두 번째 사본이 된다.
 *
 * ## 옮긴 물건은 두 자리가 다 정답이다 (6.3절 - 2026-09-09, 사용자 지시로 뒤집은 규칙)
 *
 * 한때 이 주석은 "이동된 소파는 **새 위치의 소파를 맞혀야** 정답이고, 원래 위치는 오답이어야 한다 -
 * 그게 이 게임의 난이도 그 자체다"라고 적혀 있었다. 사람이 해 보니 난이도가 아니라 **고장**으로 읽혔다.
 * 큰 물건이 옮겨졌을 때 먼저 눈에 들어오는 것은 새 자리가 아니라 비어 버린 옛 자리이고, 거기를 정확히
 * 조준해 쏜 한 발은 벽에 맞아 탄환만 잃는다(6.2절이 제거에 대해 경고한 그 "억울한 실패"다).
 *
 * 그래서 옮기기 전 자리에도 프록시를 세우고(`render/hitProxy.ts`), 이동은 **프록시로도 오브젝트로도**
 * 맞는 유일한 동사가 됐다. 관대해지는 폭은 작다 - **두 자리는 같은 한 정답의 두 조준점이지 두 번의
 * 기회가 아니다.** 판정 키가 `Change`이므로 한쪽을 맞히면 다른 쪽은 `already-found`가 된다.
 *
 * ## 왜 '이미 찾은 변화'가 별도 결과인가
 *
 * 컨셉시트 4.3은 오답 1회 = 1발이라고 못박았지만, 이미 찾은 변화를 다시 맞힌 것은 **오답이 아니다.**
 * 여기서 탄환을 깎으면 "정답을 맞혔는데 손해"라는 억울함이 생기고, 그 억울함은
 * 상태모델 12절이 동시 클릭에 대해 내린 결론("늦은 쪽은 탄환 미소모 - 오답이 아니므로 벌하지 않는다")과도 정면으로 어긋난다.
 * 그래서 결과 종류를 셋으로 나누고, 탄환 소모 여부는 `ammoCostForOutcome()` 한 곳에서만 결정한다.
 */

import { MS_PER_SECOND } from '../core/time'
import type { InstanceId, Verb } from '../core/types'
import type { Change } from './changeGen'
/**
 * 배수 규칙은 `combo.ts`가 소유한다. 값 import이지만 순환이 되지 않는다 -
 * `combo.ts`가 이 파일에서 가져가는 것은 `import type`뿐이라 런타임 그래프는 hunt → combo 한 방향이다.
 */
import { COMBO_RULES, comboMultiplierPercent, type ComboRules } from './combo'
import type { FoundEntry, GameState } from './gameState'
/** 걸기 배수는 `raise.ts`가 소유한다(찾는 사람·다녀간 사람이 같은 값). 여기서는 곱하기만 한다. */
import { raiseMultiplier } from './raise'
import type { Tuning } from './tuning'

/* --- 이 파일이 읽는 상태 조각 ------------------------------------------------------ */

/**
 * 판정이 실제로 읽는 세 필드. `GameState`가 구조적으로 만족하므로 호출부는 그대로 넘긴다.
 *
 * 좁게 받는 이유는 의존을 줄이려는 것이 아니라 **매치 리듀서가 사본 없이 같은 함수를 부르게**
 * 하기 위해서다(상태모델 11.3절 - 판정은 서버의 몫이고, 서버는 술래마다 `GameState`를 들지 않는다).
 * `phase`·`isInOriginRoom`·`ammo`가 여기 없는 것은 의도다 - 그 셋은 `canShoot`(게이트)의 것이고,
 * 매치는 자기 게이트(역할·관전·문)를 따로 갖는다. 게이트와 판정을 한 타입에 섞으면
 * 매치가 판정을 부르려고 게이트 필드를 가짜로 채우게 된다.
 */
export type ShotJudgeState = Pick<GameState, 'changes' | 'found' | 'mutated'>

/** 점수 계산이 읽는 네 필드. 같은 이유로 좁힌다. `maxCombo`·`hasPerfect`는 원래 선택 필드다. */
export type ScoreState = Pick<GameState, 'found' | 'changes' | 'maxCombo' | 'hasPerfect'>

/* --- 사격 대상 ---------------------------------------------------------------- */

/**
 * 조준선이 닿은 것. 상태모델 6.1절의 두 히트 경로와 1:1로 대응한다.
 *
 *  - `object` - 화면에 실제로 존재하는 오브젝트를 맞혔다(이동·회전·색·추가의 정답 경로)
 *  - `proxy`  - **물건이 없는 자리**를 맞혔다(`render/hitProxy.ts`가 세운 보이지 않는 타겟).
 *               제거된 자리와 **옮기기 전 자리** 둘이다(머리말)
 *
 * 두 종류를 하나로 합치지 않는 이유: 합치면 회전·색·생김의 자리에 프록시가 잘못 서 있어도 판정기가
 * 그 사실을 모른다. 종류를 들고 다니면 이 파일이 그 실수를 잡아낸다(`matchesTargetKind`).
 */
export type HitTarget =
  | Readonly<{ kind: 'object'; instanceId: InstanceId }>
  | Readonly<{ kind: 'proxy'; instanceId: InstanceId }>

/**
 * 한 발의 판정 결과.
 *
 * `already-found`를 `hit`에 합치지 않은 이유는 점수와 연출이 달라야 하기 때문이다 -
 * 득점은 없지만 벌도 없다. HUD는 "이미 찾음" 피드백을 줘야 하고, 그건 정답 연출과 달라야 한다.
 */
export type ShotOutcome =
  | Readonly<{ kind: 'hit'; change: Change }>
  | Readonly<{ kind: 'miss' }>
  | Readonly<{ kind: 'already-found' }>

/** 매 발 새 객체를 만들 이유가 없다. 얼려 두면 호출부가 실수로 변형하는 경로도 함께 닫힌다. */
const MISS: ShotOutcome = Object.freeze({ kind: 'miss' })
const ALREADY_FOUND: ShotOutcome = Object.freeze({ kind: 'already-found' })

/* --- 탄환 경제 ---------------------------------------------------------------- */

/** 오답 한 발의 값(컨셉시트 4.3: "오답 1회 = 1발 소모"). */
const AMMO_COST_MISS = 1

/** 정답·중복 사격의 값. 컨셉시트 4.3: "정답은 소모하지 않는다". */
const AMMO_COST_FREE = 0

/**
 * 이 결과가 탄환을 몇 발 먹는가.
 *
 * 상태머신(`gameState.ts`)이 이 규칙을 다시 구현하지 않도록 **판정과 같은 파일에** 둔다.
 * 규칙이 두 곳에 있으면 반드시 갈라지고, 갈라진 결과는 "왜 내 탄환이 줄었지?"라는
 * 가장 나쁜 종류의 버그가 된다(컨셉시트 4.3의 '귀책 인지' 요구와 정면 충돌).
 *
 * @param raised 걸기(M7)를 선언한 뒤인가. 참이면 **빗나감만** `raiseMissCost`배다 - 정답과 `already-found`는
 *               여전히 0발이다. 걸기가 늘리는 것은 실수의 값이지 정답의 값이 아니고, 중복 사격은 실수가 아니다
 *               (상태모델 12절 - 벌하지 않는다는 결정은 걸기 뒤에도 그대로다).
 * @param tuning `raiseMissCost`의 출처. **기본값이 없다** - `computeScore(state, tuning)`과 같은 규약이다. 한때 `TUNING`을
 *               기본값으로 두었는데, 그건 리듀서가 `deps.tuning`을 빼먹어도 컴파일이 통과하는 문이었다(`?raiseCost=`가 한쪽에서만
 *               먹는 것을 패리티 테스트가 아니라 타입이 막아야 한다). 이 파일이 `TUNING` **값**을 import 하지 않는 것이 그 증명이다.
 */
export function ammoCostForOutcome(outcome: ShotOutcome, raised: boolean, tuning: Tuning): number {
  if (outcome.kind !== 'miss') return AMMO_COST_FREE
  return raised ? AMMO_COST_MISS * tuning.raiseMissCost : AMMO_COST_MISS
}

/**
 * 빗나감 뒤의 잔탄. **0에서 멈춘다** - 걸기(M7) 뒤 비용이 2발이라 잔탄 1에서 빗나가면 -1이 되는데, 음수 잔탄은
 * `hasRoomForFullMagazine`이 "더 넉넉한 자리"로 읽어 탄창이 한 발을 잃고(`-1 + 6 = 5`) HUD는 '-1'을 그린다.
 * 마지막 빗나감은 남은 만큼만 먹는다 - 잔탄이 비용보다 적다고 사격을 막지는 않는다(잔탄 1로 쏠 수 있어야 한다는 것은
 * 컨셉시트 4.3절의 경제 그대로고, 걸기가 그것을 바꾸지 않는다). 두 리듀서(`gameState.onMiss` · `matchShoot.onMiss`)가
 * 같은 함수를 부른다 - `Math.max(0, …)`를 각자 적으면 한쪽만 고쳐지는 날이 온다.
 */
export function ammoAfterMiss(ammo: number, ammoCost: number): number {
  return Math.max(0, ammo - ammoCost)
}

/**
 * 지금 쏠 수 있는가.
 *
 * 세 조건이 전부 다른 문서에서 왔다:
 *  - 수색 페이즈에서만 (컨셉시트 3절 라운드 구조)
 *  - 정답 방에서는 불가 (컨셉시트 4.4 + 워프 규칙(`session.ts` 머리말) - 원본에는 변화가 없으므로 맞힐 것이 없다)
 *  - 탄환 0이면 불가 (컨셉시트 4.3)
 *
 * `resolveShot`이 이 검사를 품지 않는 이유: 정답 방에서 쏜 총을 `miss`로 돌려주면
 * 탄환이 깎여 버린다. "쏘면 안 되는 상황"과 "쏴서 빗맞은 상황"은 결과가 완전히 달라야 하므로
 * 게이트를 판정보다 **앞에** 둔다.
 */
export function canShoot(state: GameState): boolean {
  return state.phase === 'hunt' && !isShotBlocked(state)
}

/**
 * 수색 중인데 한 발이 **안 나가는** 이유가 있는가 - 원래 방 안(4.4절) · 탄환 0(4.3절). `canShoot`의 페이즈 밖 두 조건이고,
 * 매치 리듀서(`match/matchShoot.ts`)가 술래 좌석의 두 필드를 이 모양으로 넘겨 **같은 함수**를 부른다. 매치가 따로
 * 갖는 게이트는 역할·관전·문이지 이 두 비교식이 아니다 - 여기 한 벌만 있어야 "눌렀는데 안 나간" 조건이 두 리듀서에서 같다.
 */
export function isShotBlocked(state: Pick<GameState, 'isInOriginRoom' | 'ammo'>): boolean {
  return state.isInOriginRoom || state.ammo <= 0
}

/* --- 기하 결과 → 판정 대상 ------------------------------------------------------ */

/**
 * `input/tapPicker.ts`의 `PickResult`를 `session.ts`의 `toShotResult`가 옮겨 담는 최소 형태.
 *
 * 타입을 직접 import 하지 않는 이유는 `hitProxy.RemovalChangeLike`와 같다 -
 * 판정(순수 로직)이 레이캐스트 구현에 묶이면 서버로 옮길 때 딸려 온다.
 */
export type ShotResultLike =
  | Readonly<{ kind: 'object'; instanceId: InstanceId }>
  | Readonly<{ kind: 'removed'; instanceId: InstanceId }>
  | Readonly<{ kind: 'miss' }>

/**
 * 레이캐스트 결과를 판정 대상으로 옮긴다. 글루 코드가 이 변환을 손으로 짜지 않게 하려고 여기 둔다.
 *
 * 두 레이어가 같은 것을 다른 이름으로 부르기 때문이다:
 * 기하 레이어의 `removed`("보이지 않는 상자를 맞혔다")가 판정 레이어의 `proxy`("빈 셀 히트 경로")다.
 * 그 이름은 프록시가 제거 자리에만 서던 시절의 것이고, 지금은 옮기기 전 자리도 같은 갈래로 온다(머리말).
 * 이 매핑을 손으로 짜다 한 글자만 틀리면 **제거 동사만 조용히 전멸한다** -
 * 정답을 맞혀도 오답 처리되어 탄환까지 깎이는, 가장 알아채기 어려운 종류의 버그다.
 */
export function toHitTarget(shot: ShotResultLike): HitTarget | null {
  switch (shot.kind) {
    case 'object':
      return { kind: 'object', instanceId: shot.instanceId }
    case 'removed':
      return { kind: 'proxy', instanceId: shot.instanceId }
    case 'miss':
      return null
  }
}

/* --- 사격 판정 ---------------------------------------------------------------- */

/**
 * 맞힌 대상이 정답인지 판정한다. 상태는 바꾸지 않는다.
 *
 * 판정 순서(각 단계가 닫는 구멍을 주석으로 남긴다):
 *  1. 허공 → 오답. 벽·바닥도 호출부에서 `null`로 들어온다
 *  2. 그 id를 건드린 변화가 있는가 (5.1절 "한 오브젝트 = 최대 한 변화"이므로 있어도 하나뿐이다)
 *  3. 히트 종류가 동사와 맞는가 - 프록시는 제거·이동만, 오브젝트는 제거 말고 전부
 *  4. 그 변화가 `mutated`에 실제로 반영됐는가 - 씬이 한 프레임 뒤처져 유령을 돌려줄 수 있다
 *  5. 이미 찾았는가
 */
export function resolveShot(state: ShotJudgeState, target: HitTarget | null): ShotOutcome {
  if (target === null) return MISS

  const change = findChangeByTargetId(state.changes, target.instanceId)
  if (change === undefined) return MISS

  // 종류 불일치는 대부분 프록시 배치 실수다. 조용히 정답 처리하면
  // "이동된 물건의 원래 자리를 쏴도 맞는" 구멍이 열려 게임이 3초 만에 끝난다.
  if (!matchesTargetKind(change, target.kind)) return MISS

  if (!changeIsApplied(state, change)) return MISS

  if (isFound(state.found, change.id)) return ALREADY_FOUND

  return { kind: 'hit', change }
}

/**
 * 이 변화를 이미 찾았는가. 발견 목록은 12개 이하이므로 선형 탐색으로 충분하다.
 * 공개하지 않는 이유: 상태 단위의 같은 질문은 `gameState.isChangeFound`가 이미 답한다.
 */
function isFound(found: readonly FoundEntry[], changeId: string): boolean {
  for (const entry of found) {
    if (entry.changeId === changeId) return true
  }
  return false
}

/**
 * 5.1절("한 물건은 한 번만 건드릴 수 있다") 덕분에 targetId → 변화가 1:1이다.
 * 이 전제가 깨지면 판정 단위 자체가 무너지므로, 여기서 조용히 첫 번째를 고르지 않고 시끄럽게 터뜨린다.
 */
function findChangeByTargetId(
  changes: readonly Change[],
  targetId: InstanceId,
): Change | undefined {
  let found: Change | undefined

  for (const change of changes) {
    if (change.targetId !== targetId) continue
    if (found !== undefined) {
      throw new Error(
        `resolveShot: 한 오브젝트에 변화가 둘 이상이다 (targetId='${targetId}', ` +
          `changeId='${found.id}' / '${change.id}'). 상태모델 5.1절 위반 - 변화 생성기의 버그다.`,
      )
    }
    found = change
  }

  return found
}

/**
 * 빈 자리에 프록시가 서는 동사. `render/hitProxy.buildHitProxySpecs`의 같은 목록이다 -
 * 한쪽만 늘어나면 "맞혔는데 오답"이나 "엉뚱한 자리가 정답"이 되므로 둘 다 고칠 때만 늘린다.
 */
const PROXIED_VERBS: ReadonlySet<Verb> = new Set<Verb>(['remove', 'move'])

/**
 * 히트 종류와 동사가 맞는가.
 *
 * 프록시로 맞는 것은 제거와 이동뿐이고(빈 자리가 생기는 두 동사다), 오브젝트로 맞는 것은 제거 말고 전부다
 * (제거된 물건은 화면에 없다). 이동만 양쪽에 걸치는데 그것이 "두 자리 다 정답"의 정확한 뜻이다.
 *
 * `hitProxy.ts`가 그 두 자리에만 프록시를 세우므로 정상 동작에서는 이 검사가 항상 통과한다 -
 * 즉 이 함수는 **판정 로직이 아니라 두 모듈 사이의 안전장치**다. 통과하지 못하면 프록시가 잘못 세워진 것이다.
 */
function matchesTargetKind(change: Change, kind: HitTarget['kind']): boolean {
  return kind === 'proxy' ? PROXIED_VERBS.has(change.verb) : change.verb !== 'remove'
}

/**
 * 변화가 `mutated`에 실제로 반영됐는가.
 *
 * **히트 종류가 아니라 동사로 묻는다.** 제거만 변경본에 없어야 하고 나머지는 있어야 한다 -
 * 이동은 프록시로 맞아도(옛 자리) 물건 자체는 새 자리에 살아 있으므로, 히트 종류로 물으면
 * 옛 자리를 맞힌 정답이 통째로 오답이 된다.
 *
 * 오브젝트 히트인데 변경본에 없다면 씬이 상태보다 뒤처진 것이다(제거된 소품의 메시가 아직 남아 있는 프레임).
 * 그 유령을 정답으로 인정하면 "화면에서 이미 사라진 물건을 쏴서 제거 정답을 먹는" 경로가 생긴다 -
 * 6.2절이 프록시로 따로 만들어 준 관대한 판정을, 뒷문으로 한 번 더 여는 셈이다.
 */
function changeIsApplied(state: ShotJudgeState, change: Change): boolean {
  const isInMutated = state.mutated.objects.has(change.targetId)
  return change.verb === 'remove' ? !isInMutated : isInMutated
}

/* --- 점수 --------------------------------------------------------------------- */

/**
 * 발견 1개당 기본 점수.
 *
 * 이 절의 상수는 전부 컨셉시트 7절이 "플레이테스트로만 답이 나온다"고 한 배점이다.
 * 라운드 규칙 수치(변화 12개·탄환 16발·수색 5분 등)는 `tuning.ts`가 유일한 출처지만,
 * 배점은 이 파일 밖에서 쓰이지 않으므로 여기 모아 둔다 - 흩어뜨리지만 않으면 된다.
 * 예외가 배수 곡선인데, 그것은 점수만의 것이 아니라 HUD·사운드도 읽으므로 `combo.ts`가 소유한다.
 */
const POINTS_PER_FIND = 100

/**
 * 콤보 기록이 없는 발견을 읽는 값. `combo.ts`의 `NO_COMBO`와 숫자는 같지만 뜻이 다르다
 * ("콤보가 없었다"가 아니라 "기록이 없다"). 어느 쪽이든 배수는 1.0이 되어 점수를 부풀리지 않는다.
 */
const NO_RECORDED_COMBO = 0

/**
 * 속도 보너스 구간 개수. 수색 시간 전체를 이만큼으로 등분한다.
 *
 * 상태모델 12절: **속도 보너스를 밀리초 경쟁으로 만들지 않는다.**
 * 네트워크 레이턴시가 순위를 가르면 실력 게임이 아니라 회선 게임이 된다.
 * 수색 5분 기준 구간 하나가 50초이므로, 0.1초 차이로 점수가 갈리는 일은
 * 구간 경계에 정확히 걸치는 경우 말고는 존재하지 않는다.
 */
const SPEED_BONUS_BUCKETS = 6

/**
 * 가장 빠른 구간의 보너스. **콤보 도입과 함께 50에서 20으로 내렸다.**
 *
 * 압박이 둘이면 안 되기 때문이다. 콤보는 "틀리지 마라"라고 말하고 속도 보너스는 "서둘러라"라고
 * 말하는데, 이 게임에서 서두르는 것은 **덜 관찰하는 것**이고 덜 관찰하면 오답이 늘어
 * 콤보가 끊긴다. 두 장치가 서로를 취소하면 남는 것은 혼란뿐이다.
 *
 * 무게를 정확도 쪽에 싣는다: 발견 하나가 콤보로 최대 +200점(3.0배)을 얹는 반면
 * 속도는 최대 +20점이다. 그래도 0으로 만들지 않는 이유는 종료 조건이다 -
 * 속도 보너스가 아예 없으면 5분을 다 쓰는 것이 언제나 최적이 되어, 다 찾은 뒤에도
 * 남은 시간을 멍하니 흘려보내는 판이 생긴다.
 */
const SPEED_BONUS_TOP = 20

/** 구간이 하나 늦어질 때마다 깎이는 양. 6구간이므로 20 → 16 → 12 → 8 → 4 → 0이 된다. */
const SPEED_BONUS_STEP = 4

/**
 * 퍼펙트(무오답 전량 발견) 보너스.
 *
 * 이 게임에서 가장 드문 사건이므로 **눈에 띄는 덩어리**여야 한다. 발견 5개 값(500점)으로 잡았다 -
 * 변화 12개짜리 만점 판이 대략 3200점이니 총점의 6분의 1쯤이다.
 * 더 키우면 "한 번 틀린 판은 버리고 다시 시작"이 합리적인 선택이 되어 8절의 판정 지표
 * ("한 판 더")를 오히려 갉아먹는다. 더 줄이면 결과 화면의 한 줄로 끝나 축하가 되지 않는다.
 *
 * 변화 개수에 비례시키지 않은 것도 의도다. 최고 점수는 단계별로 따로 저장되므로
 * (`progress.ts`) 단계 사이의 점수를 견줄 일이 없고, 비례식은 설명해야 하는 손잡이를 하나 더 만든다.
 */
const PERFECT_BONUS = 500

/** 퍼센트 배수를 점수로 환산하는 분모. `combo.ts`가 정수 퍼센트로 배수를 들고 있기 때문이다. */
const PERCENT_SCALE = 100

/**
 * 점수 내역. 결과 화면이 "발견 8개 × 100 + 콤보 640 + 속도 96 + 퍼펙트 500"처럼 풀어 보여줄 수
 * 있어야 플레이어가 다음 판에 무엇을 바꿔야 할지 안다 - 그게 "한 판 더"의 재료다.
 *
 * **콤보 기여분을 기본 점수에 섞지 않는 것이 이 타입의 핵심이다.** 섞으면 "발견 8개 × 100"이
 * 1240 같은 숫자가 되어 산수가 맞지 않는 표가 되고, 무엇보다 **콤보가 얼마를 벌어 줬는지**가
 * 사라진다. 그 숫자를 보여 주지 않으면 콤보는 화면에서 반짝이는 장식일 뿐 전략이 되지 않는다.
 */
export type ScoreBreakdown = Readonly<{
  findCount: number
  /** 배수를 빼고 센 기본 점수(발견 수 × 100). */
  findPoints: number
  /** 배수가 기본 점수 위에 얹어 준 몫. 콤보가 한 번도 안 쌓였으면 0이다. */
  comboBonus: number
  /**
   * 걸기(M7) 뒤의 발견들이 얹어 준 몫 = 그 발견들의 `findPointsFor` 한 벌 더. 걸지 않았거나 선언 뒤 발견이
   * 없으면 0이다. 콤보 몫과 갈라 두는 이유는 `comboBonus`와 같다 - "걸어서 얼마를 벌었나"가 보여야 다음 판에
   * 걸지 말지를 정할 수 있다. 합계는 `findPointsForEntry` 한 곳에서 나오므로 자막의 "+480"과 여기 표가 같다.
   */
  raisedBonus: number
  speedBonus: number
  /** 무오답 전량 발견 보너스. 아니면 0. */
  perfectBonus: number
  /** 이 라운드에 도달한 최고 콤보. 결과 화면의 자랑거리다. */
  maxCombo: number
  isPerfect: boolean
  total: number
}>

/**
 * 술래 점수(컨셉시트 4.5: 발견당 득점 + 속도 보너스 + **콤보 배수** + **퍼펙트**).
 *
 * **미발견은 감점하지 않는다.** 오답도 감점하지 않는다 -
 * 오답의 대가는 이미 탄환으로 치렀고(4.3절), 이제 콤보까지 치른다. 거기에 감점을 더하면
 * 컨셉시트 5절이 노리는 "실패가 웃긴" 톤이 "실패가 억울한" 톤으로 뒤집힌다.
 * 콤보가 벌이 아니라 **못 받은 보너스**인 것이 중요하다 - 점수판의 숫자는 언제나 위로만 움직인다.
 */
export function computeScore(state: ScoreState, tuning: Tuning): number {
  return computeScoreBreakdown(state, tuning).total
}

/**
 * 발견 한 건의 점수. 콤보 배수가 여기서 곱해진다.
 *
 * HUD가 발견 순간에 "+240"을 띄우려면 점수 규칙 하나를 나눠 써야 하는데, 그것을 연출 쪽에서
 * 다시 구현하면 결과 화면의 합계와 어긋난다. 어긋난 숫자는 사람이 반드시 알아채고,
 * 그때부터 점수판 전체를 믿지 않는다.
 */
export function findPointsFor(combo: number, rules: ComboRules = COMBO_RULES): number {
  // 정수 퍼센트로 곱하고 나눈다. 실수 배수(1.2)를 곱하면 오차가 총점까지 따라온다.
  return Math.round((POINTS_PER_FIND * comboMultiplierPercent(combo, rules)) / PERCENT_SCALE)
}

/**
 * 발견 기록 한 줄의 점수 - 콤보 배수 위에 걸기 배수(`raise.ts`)까지 곱한 **그 발견이 실제로 벌어 준 값**이다.
 *
 * 자막(`sessionEffects.hitJuiceArgs`)과 결과 내역(`computeScoreBreakdown`)이 **이 함수 하나**를 부른다. 걸기 배수를
 * 자막 쪽에서 따로 곱하면 결과 화면의 합계와 어긋나는 날이 오고, 어긋난 숫자는 사람이 반드시 알아챈다.
 * 기록이 없는 콤보는 콤보 없음으로, 기록이 없는 걸기는 걸지 않은 것으로 읽는다 - 모르는 값으로 점수를 부풀리지 않는다.
 */
export function findPointsForEntry(
  entry: Pick<FoundEntry, 'combo' | 'raised'>,
  rules: ComboRules = COMBO_RULES,
): number {
  return findPointsFor(entry.combo ?? NO_RECORDED_COMBO, rules) * raiseMultiplier(entry.raised === true)
}

/**
 * @param rules 배수 규칙. 기본값 말고 다른 값을 넣는 곳은 지금은 테스트뿐이다 -
 *              `Tuning`에 넣지 않은 이유는 배점이 `tuning.ts`가 아니라 이 두 파일의 것이기 때문이다.
 */
export function computeScoreBreakdown(
  state: ScoreState,
  tuning: Tuning,
  rules: ComboRules = COMBO_RULES,
): ScoreBreakdown {
  const bucketMs = speedBonusBucketMs(tuning.huntSeconds)
  const scored = new Set<string>()

  let findCount = 0
  let comboBonus = 0
  let raisedBonus = 0
  let speedBonus = 0

  for (const entry of state.found) {
    if (!isKnownChange(state.changes, entry.changeId)) {
      // 존재하지 않는 변화를 발견 목록에 넣은 것은 상태머신의 버그다.
      // 조용히 점수를 주면 "왜 점수가 이상하지"로만 드러나 원인 추적이 불가능해진다.
      console.warn(
        `[hunt] computeScore: 변화 목록에 없는 changeId '${entry.changeId}'를 발견 목록에서 무시한다.`,
      )
      continue
    }

    if (scored.has(entry.changeId)) {
      // 상태모델 12절: 동시 클릭 시 늦은 쪽은 득점하지 않는다(벌하지도 않는다).
      // 싱글에서는 나올 수 없는 입력이므로 경고를 남긴다.
      console.warn(`[hunt] computeScore: 중복 발견 '${entry.changeId}'는 한 번만 계산한다.`)
      continue
    }

    scored.add(entry.changeId)
    findCount += 1
    /*
     * **배수는 발견 시점의 콤보로 굳는다.** 나중 콤보로 소급 계산하지 않는 이유가 규칙의 전부다 -
     * 소급하면 오답으로 끊긴 앞부분까지 되살아나 "끊겼다"는 사건이 점수에서 사라진다.
     * 기록이 없는 항목(개편 이전 픽스처)은 콤보 없음으로 읽는다. 없는 값을 후하게 해석해
     * 점수를 부풀리는 것보다, 낮게 읽고 티가 나는 편이 낫다.
     */
    const comboPoints = findPointsFor(entry.combo ?? NO_RECORDED_COMBO, rules)
    comboBonus += comboPoints - POINTS_PER_FIND
    /*
     * 걸기(M7)도 발견 시점의 값으로 굳는다(`FoundEntry.raised`). 선언 뒤의 발견만 한 벌 더 받고, 그 "한 벌"은
     * `findPointsForEntry`가 정한다 - 여기서 `× 2`를 적으면 자막과 내역이 다른 배수를 갖게 되는 자리다.
     */
    raisedBonus += findPointsForEntry(entry, rules) - comboPoints
    speedBonus += speedBonusFor(entry.foundAtMs, bucketMs)
  }

  const findPoints = findCount * POINTS_PER_FIND
  const isPerfect = isPerfectResult(state, findCount)
  const perfectBonus = isPerfect ? PERFECT_BONUS : 0

  return {
    findCount,
    findPoints,
    comboBonus,
    raisedBonus,
    speedBonus,
    perfectBonus,
    maxCombo: state.maxCombo ?? NO_RECORDED_COMBO,
    isPerfect,
    total: findPoints + comboBonus + raisedBonus + speedBonus + perfectBonus,
  }
}

/**
 * 퍼펙트인가. **상태의 깃발과 실제 발견 수를 둘 다 본다.**
 *
 * 깃발(`hasPerfect`)은 리듀서가 마지막 발견의 순간에 세우고, 발견 수는 점수 계산이 방금 센 값이다.
 * 둘 중 하나만 믿지 않는 이유: 깃발만 믿으면 발견 목록이 손상된 상태(존재하지 않는 changeId 등)에서도
 * 보너스가 나가고, 발견 수만 믿으면 오답을 열 번 낸 완주도 퍼펙트가 된다.
 * 500점짜리 보너스는 두 증거가 일치할 때만 준다.
 */
function isPerfectResult(state: ScoreState, findCount: number): boolean {
  if (state.hasPerfect !== true) return false
  return findCount > 0 && findCount === state.changes.length
}

/**
 * 구간 하나의 길이(밀리초). 수색 시간이 바뀌면 보너스 곡선도 같이 늘어나야
 * "빨리 찾을수록 이득"이라는 체감이 튜닝 값과 무관하게 유지된다.
 */
function speedBonusBucketMs(huntSeconds: number): number {
  if (!Number.isFinite(huntSeconds) || huntSeconds <= 0) {
    throw new RangeError(
      `computeScore: tuning.huntSeconds는 양수여야 한다 (받은 값: ${huntSeconds}).`,
    )
  }

  return (huntSeconds * MS_PER_SECOND) / SPEED_BONUS_BUCKETS
}

/**
 * 발견 시각 → 속도 보너스.
 *
 * `foundAtMs`는 **수색 페이즈 시작 이후 경과 밀리초**다(상태머신이 `phaseElapsedMs`를 그대로 기록한다).
 * 절대 시각(`Date.now()`)이 아니다 - 절대 시각을 쓰면 점수가 결정론을 잃는다.
 */
function speedBonusFor(foundAtMs: number, bucketMs: number): number {
  if (!Number.isFinite(foundAtMs) || foundAtMs < 0) {
    throw new RangeError(
      `computeScore: foundAtMs는 0 이상의 유한수여야 한다 (받은 값: ${foundAtMs}). ` +
        '수색 페이즈 경과 시간을 그대로 넣어야 한다.',
    )
  }

  // 마지막 구간을 넘겨도 음수 보너스가 되지 않게 자른다(감점 없음 원칙).
  const bucket = Math.min(Math.floor(foundAtMs / bucketMs), SPEED_BONUS_BUCKETS - 1)
  return Math.max(SPEED_BONUS_TOP - bucket * SPEED_BONUS_STEP, 0)
}

function isKnownChange(changes: readonly Change[], changeId: string): boolean {
  for (const change of changes) {
    if (change.id === changeId) return true
  }
  return false
}
